export const REALTIME_EVENT_TYPES = [
  "request.created",
  "request.sent",
  "request.verified_access",
  "request.viewed",
  // `Signing Started` is currently a product/journey milestone derived from
  // the first durable `request.signing_progress` evidence rather than a
  // dedicated realtime event name.
  "request.signing_progress",
  "request.signed",
  "request.declined",
  "request.revoked",
  "request.expired",
  "request.reminded",
  "user.first_send_completed",
] as const;

export type RealtimeEventType = (typeof REALTIME_EVENT_TYPES)[number];

export const WEBHOOK_EVENT_TYPES = [
  "request.created",
  "request.sent",
  "request.viewed",
  "request.signed",
  "request.declined",
  "request.revoked",
  "request.expired",
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export const WEBHOOK_EVENT_SCOPE_DECISION =
  "External webhooks stay request-lifecycle scoped. Realtime-only journey signals and internal follow-through milestones are not part of the default webhook contract." as const;

export const WEBHOOK_EVENT_EXCLUDED_TYPES = [
  "request.verified_access",
  "request.signing_progress",
  "request.reminded",
  "user.first_send_completed",
  "request.artifacts_ready",
  "request.follow_through_updated",
] as const;

export type ExcludedWebhookEventType = (typeof WEBHOOK_EVENT_EXCLUDED_TYPES)[number];

export const WEBHOOK_EVENT_EXCLUSION_REASONS: Record<ExcludedWebhookEventType, string> = {
  "request.verified_access":
    "Verified Access is currently a sender-facing realtime trust signal, not part of the external webhook contract.",
  "request.signing_progress":
    "Signing progress backs the derived Signing Started milestone inside realtime product surfaces and is not emitted as an external webhook.",
  "request.reminded":
    "Reminder sends are internal operational signals until there is a stronger external automation need.",
  "user.first_send_completed":
    "User-first-send milestones are internal growth telemetry, not request lifecycle webhooks.",
  "request.artifacts_ready":
    "Artifact readiness is currently represented through authenticated request routes and follow-through state rather than a dedicated webhook event.",
  "request.follow_through_updated":
    "Follow-through updates remain internal operational state until a more mature external automation contract exists.",
};

export const WEBHOOK_EVENT_DESCRIPTIONS: Record<WebhookEventType, string> = {
  "request.created":
    "A signing request record was created and recipient delivery is about to begin.",
  "request.sent":
    "Recipient delivery completed for the signing request.",
  "request.viewed":
    "A recipient opened the signing request for the first time.",
  "request.signed":
    "A recipient completed signing. Use allComplete=true in the payload to detect final completion.",
  "request.declined":
    "A recipient declined to sign the request.",
  "request.revoked":
    "The sender voided the signing request before completion.",
  "request.expired":
    "The signing request expired before completion.",
};

export const SKILLS_EVENT_TYPES = [
  "document.uploaded",
  "request.viewed",
  "request.signed",
  "request.reminder.due",
] as const;

export type SkillsEventType = (typeof SKILLS_EVENT_TYPES)[number];

export const DEPRECATED_EVENT_ALIASES = {
  "request.completed": "request.signed",
  "request.reminder_sent:realtime": "request.reminded",
} as const;

export type DeprecatedEventAlias = keyof typeof DEPRECATED_EVENT_ALIASES;
export type CanonicalEventType = RealtimeEventType | WebhookEventType | SkillsEventType;

const REALTIME_EVENT_TYPE_SET = new Set<string>(REALTIME_EVENT_TYPES);
const WEBHOOK_EVENT_TYPE_SET = new Set<string>(WEBHOOK_EVENT_TYPES);
const SKILLS_EVENT_TYPE_SET = new Set<string>(SKILLS_EVENT_TYPES);

export function isRealtimeEventType(value: unknown): value is RealtimeEventType {
  return typeof value === "string" && REALTIME_EVENT_TYPE_SET.has(value);
}

export function isWebhookEventType(value: unknown): value is WebhookEventType {
  return typeof value === "string" && WEBHOOK_EVENT_TYPE_SET.has(value);
}

export function isSkillsEventType(value: unknown): value is SkillsEventType {
  return typeof value === "string" && SKILLS_EVENT_TYPE_SET.has(value);
}

export function toCanonicalEventType(value: DeprecatedEventAlias): (typeof DEPRECATED_EVENT_ALIASES)[DeprecatedEventAlias];
export function toCanonicalEventType(value: string): string;
export function toCanonicalEventType(value: string): string {
  return DEPRECATED_EVENT_ALIASES[value as DeprecatedEventAlias] ?? value;
}
